<script setup lang="ts">
/**
 * 画像・動画投稿コンポーネント。
 * APIへリクエスト送付も行い、Storeへ投稿された画像・動画IDを保持する。
 * バリデーションエラー表示にも対応。
 */

import { defineProps } from "vue";
import { ApiPostLoadStatus, ApiPostLoadStatusType } from "@/modules/types/apiLoadStatusType";
import { PropType, reactive, toRefs, inject, defineEmits } from "vue";
import ApiPostLoadMaster from "@/components/common/api/ApiPostLoadMaster.vue";
import MediaController from "@/controllers/communityTalk/mediaController";
import { CommunityTalkStore, isValidCommunityTalkStoreKey } from "@/store/communityTalk/communityTalkStore";
import CustomLabel from "./CustomLabel.vue";
import MediaUtil from "@/modules/utils/mediaUtil";
import { PostMediaViewModel } from "@/viewModels/communityTalk/postMediaViewModel";

const mediaController = new MediaController();

const props = defineProps({
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: {
		type: String,
		required: true,
		default: "",
	},
	/**
	 * 投稿種類。
	 * トークルームとして: `TalkRoom`
	 * リプライとして: `Reply`
	 */
	postType: {
		type: String,
		required: true,
		default: "TalkRoom",
		validator(val: string) {
			return ["TalkRoom", "Reply"].includes(val);
		},
	},
	/**
	 * 入力欄を無効化するかどうか。
	 */
	disabled: {
		type: Boolean,
		required: false,
		default: false,
	},
	/**
	 * 表示時の初期値。
	 */
	initPostMediaViewModels: {
		type: Object as PropType<PostMediaViewModel[]>,
		required: true,
	},
});

const name = props.postType === "TalkRoom" ? "ProfilePicId" : "reply.ProfilePicId";

interface State {
	/**
	 * inputHTMLの内容。
	 */
	inputElement: HTMLInputElement | null;
	/**
	 * 画像・動画投稿APIステータス。
	 */
	status: ApiPostLoadStatusType;
	/**
	 * 画像・動画投稿APIエラーメッセージ。
	 */
	errorMessage: string;
	/**
	 * フォーム多重送信制御用フラグ
	 */
	isProcessing: boolean;
	/**
	 * 画像・動画投稿キャンセルフラグ。
	 */
	isCanceledUpload: boolean;
	/**
	 * 親コンポーネントからのAPIエラーメッセージ。
	 */
	parentErrorMessage: string;
	/**
	 * 画像・動画投稿のプレビュー用ViewModel。
	 */
	postMediaViewModels: PostMediaViewModel[];
}
// reactiveな変数宣言
const state = reactive<State>({
	inputElement: null,
	status: ApiPostLoadStatus.beforeRequest,
	errorMessage: "",
	isProcessing: false,
	isCanceledUpload: false,
	parentErrorMessage: "",
	postMediaViewModels: props.initPostMediaViewModels,
});
const { inputElement, status, errorMessage, isProcessing, isCanceledUpload, parentErrorMessage } = toRefs(state);

// Emits
interface Emits {
	// 画像・動画アップロード完了イベント
	(e: "on-success-upload-medias", v: PostMediaViewModel[]): void;
	// 画像・動画の選択を解除したイベント
	(e: "on-reset-medias"): void;
	// 画像・動画の投稿が開始されたイベント
	(e: "on-start-post-process"): void;
	// 画像・動画の投稿が完了したイベント
	(e: "on-end-post-process"): void;
}
const emits = defineEmits<Emits>();

// Storeの初期化処理
if (!isValidCommunityTalkStoreKey(props.communityTalkStoreKey)) {
	// なんらかの原因でInjectKeyにエラーが発生していた場合は強制終了
	throw new Error(`InjectKeyにエラーが発生しました。${props.communityTalkStoreKey.toString()}`);
}
const communityTalkStore = inject(props.communityTalkStoreKey) as CommunityTalkStore;

// ファイル選択ボタン押下時の処理
const onClickInputFileButton = (): void => {
	if (state.inputElement) {
		state.inputElement.click();
	}
};

// 画像・動画をアップロード
const upload = async (): Promise<void> => {
	// ファイルが存在しなければ処理を空振り
	if (!state.inputElement) return;
	if (!state.inputElement.files) return;

	state.isProcessing = true;
	state.isCanceledUpload = false;
	state.status = ApiPostLoadStatus.loading;
	emits("on-start-post-process");

	try {
		const fileList = state.inputElement.files;
		const files = Array.from(fileList);

		// 画像・動画ファイルのバリデーションチェック
		const validErrorMessages = await mediaController.checkPictureAndMovie(files);
		if (validErrorMessages.length !== 0) {
			state.errorMessage = validErrorMessages.join("\n");
			return;
		}

		// 投稿がキャンセルされてなければ、対象画像・動画投稿を作成&親コンポーネントに通知
		if (!state.isCanceledUpload) {
			const viewModels = await mediaController.createMedias(files, communityTalkStore.getTenantId());
			state.postMediaViewModels = viewModels;
			emits("on-success-upload-medias", viewModels);
		}
		state.errorMessage = "";
	} catch (e) {
		const otherErrorMessage = "写真・動画のアップロードに失敗しました。";
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if (e && (e as any).message) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			state.errorMessage = (e as any).message;
		} else {
			// エラー内容が多岐にわたるため、一律でエラーメッセージを統一
			state.errorMessage = otherErrorMessage;
		}
	} finally {
		// 成功しても失敗しても、再度画像を投稿するようにしたいのでAPIステータスは投稿前のステータスへ
		state.status = ApiPostLoadStatus.beforeRequest;
		state.isProcessing = false;
		emits("on-end-post-process");
		// 投稿がキャンセルされている場合、親コンポーネントに通知
		if (state.isCanceledUpload) {
			state.postMediaViewModels = [];
			emits("on-reset-medias");
		}
		state.isCanceledUpload = false;
	}
};

// 画像・動画のアップロードキャンセル処理
const cancelUpload = (): void => {
	state.isCanceledUpload = true;
	state.errorMessage = "";
	state.status = ApiPostLoadStatus.beforeRequest;
};

// 画像・動画の登録解除
const detachMedia = (): void => {
	// inputタグ内部のファイルデータを削除
	if (!state.inputElement) return;
	state.inputElement.value = "";
	// 登録解除を親コンポーネントに通知
	state.postMediaViewModels = [];
	emits("on-reset-medias");
};

// 画像・動画のプレビューエリアのClass
const previewMediaClass = (viewModel: PostMediaViewModel): string => {
	const baseClassName = "revico-community-talk-post-media-preview--";
	switch (viewModel.type) {
		case "Image":
			return `${baseClassName}img`;
		case "Movie":
			return `${baseClassName}thumbnail`;
		default:
			throw new Error(`想定外のtypeが渡されました。type: ${viewModel.type}`);
	}
};
</script>

<template>
	<div class="revico-community-talk-post-media">
		<api-post-load-master :status="status">
			<template #beforeRequest>
				<custom-label class="revico-community-talk-post-media--label" text="写真・動画アップロード" :disabled="disabled" />
				<div class="revico-community-talk-post-media-wrapper">
					<button type="button" class="revico-community-talk-post-media--input-button" :disabled="disabled || isProcessing" @click="onClickInputFileButton">ファイルを選択</button>
					<input
						ref="inputElement"
						@change="upload"
						type="file"
						:accept="MediaUtil.validExtensions.join()"
						:name="name"
						:disabled="disabled || isProcessing"
						class="revico-community-talk-post-media--input"
						multiple
					/>
					<div class="revico-community-talk-post-media-canceling" v-if="isCanceledUpload && isProcessing">アップロードをキャンセルしています…</div>

					<!-- 画像・動画投稿時の注意文言 -->
					<ul class="revico-community-talk-post-media-description">
						<li class="revico-community-talk-post-media-description--item">
							画像を複数枚アップロードする場合は、アップロードする画像をまとめて選択してください。(上限{{ mediaController.maxImageCount }}枚)
						</li>
						<li class="revico-community-talk-post-media-description--item">アップロードできる画像拡張子は、png/jpg/jpeg/gif(静止画)です。</li>
						<li class="revico-community-talk-post-media-description--item">画像サイズの上限は、1枚あたり{{ MediaUtil.maxFileSize.image / 1024 / 1024 }}MBです。</li>
					</ul>
					<ul class="revico-community-talk-post-media-description">
						<li class="revico-community-talk-post-media-description--item">動画は{{ mediaController.maxMovieCount }}つのみアップロードできます。</li>
						<li class="revico-community-talk-post-media-description--item">アップロードできる動画拡張子は、mp4/movです。</li>
						<li class="revico-community-talk-post-media-description--item">
							動画サイズおよび再生時間の上限は、それぞれ{{ MediaUtil.maxFileSize.movie / 1024 / 1024 }}MB、{{ MediaUtil.maxMoviePlayTime }}秒です。
						</li>
					</ul>

					<!-- 画像・動画のプレビュー -->
					<p class="revico-community-talk-post-media-preview" v-if="state.postMediaViewModels.length > 0">
						<span class="revico-community-talk-post-media-preview-media-wrapper">
							<template v-for="(viewModel, i) in state.postMediaViewModels" :key="i">
								<div :class="previewMediaClass(viewModel)">
									<img :src="viewModel.mediaUrl" @error="detachMedia" />
									<!-- 動画サムネイル用再生ボタン -->
									<div v-if="viewModel.type === 'Movie'" class="revico-community-talk-post-media-preview--thumbnail-play-button"></div>
								</div>
							</template>
						</span>
						<!-- プレビュー解除ボタン -->
						<button class="revico-community-talk-post-media-preview--detach-button" type="button" @click="detachMedia">選択を解除</button>
					</p>

					<!-- APIエラーメッセージ -->
					<div class="revico-community-talk-post-media-error-message" v-if="errorMessage">
						{{ errorMessage }}
					</div>
					<!-- 親コンポーネントからのAPIエラーメッセージ -->
					<div class="revico-community-talk-post-media-error-message" v-if="parentErrorMessage">
						{{ parentErrorMessage }}
					</div>
				</div>
			</template>
			<template #loading>
				<!-- 画像・動画投稿キャンセルボタン -->
				<button v-if="isProcessing" type="button" class="revico-community-talk-post-media--cancel-button" @click="cancelUpload">写真・動画の投稿をキャンセル</button>
			</template>
		</api-post-load-master>
	</div>
</template>

<style lang="scss">
.revico-widget .revico-community-talk .revico-community-talk {
	&-post-media {
		&--label {
			margin-top: 16px;
		}
		&-wrapper {
			padding: 12px;
			background-color: #fff;
		}

		&--input {
			display: none;
		}
		&--input-button,
		&--cancel-button {
			@include communitytalk_input(#fff);
			padding: 6px;
			background-color: var(--base-bg-color);
			cursor: pointer;
			resize: none;
			@include disabledInput;
		}
		&--cancel-button {
			border: 3px solid white;
		}
		&-canceling {
			margin: 6px 0;
			font-size: 11px;
			text-align: center;
		}

		&-description {
			width: 100%;
			margin-top: 6px;
			font-size: 8px;
			&--item::before {
				content: "※";
			}
		}

		&-preview {
			width: 100%;
			margin-top: 12px;
			background-color: #fff;
			text-align: center;
			&-media-wrapper {
				display: flex;
				flex-wrap: wrap;
				justify-content: center;
			}
			&--img,
			&--thumbnail {
				display: flex;
				align-items: center;
				justify-content: center;
				width: 90px;
				height: 90px;
				margin-right: 6px;
				object-fit: cover;
				transition: 0.1s;
				img {
					max-width: 100%;
					max-height: 100%;
				}
			}
			&--thumbnail {
				position: relative;
				&-play-button {
					@include moviePlayButton;
				}
			}
			&--detach-button {
				display: inline-block;
				width: 100%;
				margin-top: 8px;
				padding: 4px;
				color: var(--base-font-color);
				font-size: 12px;
				background-color: var(--base-bg-color);
				border: none;
				cursor: pointer;
			}
		}

		&-error-message {
			margin-top: 12px;
			color: var(--base-error-message-font-color);
			font-size: 0.9em;
			white-space: pre-wrap;
		}
	}
}
</style>
